'use client';

import { SwitchInput } from '@app/app/_components/Input/SwitchInput';
import { ChangeEvent, ReactNode, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTranslations } from 'next-intl';
import { RangeSliderUnit } from '@app/domain/model/RangeSliderUnit';

interface Props {
    name?: string;
    title?: string;
    headerActive?: boolean;
    additionalContent?: ReactNode;
    switchName?: string;
    switchDefaultChecked?: boolean;
    switchDisabled?: boolean;
    value?: number;
    defaultValue?: number;
    min?: number;
    max?: number;
    step?: number;
    unit?: RangeSliderUnit;
    minSuggested?: number;
    className?: string;
    onChange?: (value: number) => void;
    onSwitchChange?: (checked: boolean) => void;
    form?: string;
    disabled?: boolean;
    footerContent?: ReactNode;
}

export function RangeSlider({
    name,
    title,
    headerActive = false,
    additionalContent,
    switchName,
    switchDefaultChecked = false,
    switchDisabled = false,
    value,
    defaultValue = 0,
    min = 0,
    max = 100,
    step = 1,
    unit,
    minSuggested,
    className,
    onChange,
    onSwitchChange,
    form,
    disabled = false,
    footerContent,
}: Props) {
    const t = useTranslations();
    const [internalValue, setInternalValue] = useState(value ?? defaultValue);
    const currentValue = value ?? internalValue;

    const handleRangeChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const newValue = Number(e.target.value);
            setInternalValue(newValue);
            onChange?.(newValue);
        },
        [onChange],
    );

    const handleSwitchChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            onSwitchChange?.(e.target.checked);
        },
        [onSwitchChange],
    );

    const formatValue = useCallback(
        (val: number) => {
            if (!unit) {
                return val.toLocaleString();
            }

            if (unit === RangeSliderUnit.Euro) {
                return `${val.toLocaleString('lt')} ${t('general.units.euro')}`;
            }

            if (unit === RangeSliderUnit.Years) {
                const unitLabel = val === 1 ? t('general.units.year') : t('general.units.years');
                return `${val.toLocaleString()} ${unitLabel}`;
            }

            return val.toLocaleString();
        },
        [unit, t],
    );

    return (
        <div className={twMerge('rounded-lg border border-neutral-200 bg-white p-6', className)}>
            {headerActive && (
                <div className="mb-6 flex items-start justify-between border-b border-neutral-150 pb-8">
                    <div className="flex flex-col gap-2">
                        {title && <h3 className="text-lg">{title}</h3>}
                        {additionalContent}
                    </div>
                    {switchName && (
                        <SwitchInput
                            name={switchName}
                            defaultChecked={switchDefaultChecked}
                            onChange={handleSwitchChange}
                            disabled={switchDisabled}
                        />
                    )}
                </div>
            )}

            <div className="flex flex-col items-center gap-6">
                <div className="mb-4 text-center">
                    <span className="text-4xl text-neutral-700">{formatValue(currentValue)}</span>
                </div>

                <div className="relative w-full">
                    <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-between">
                        {Array.from({ length: 17 }, (_, i) => (
                            <div
                                key={i}
                                className="h-3 w-px bg-neutral-700"
                                style={{
                                    transform: 'translateX(-50%)',
                                }}
                            />
                        ))}
                    </div>

                    <input
                        type="range"
                        name={name}
                        form={form}
                        min={min}
                        max={max}
                        step={step}
                        value={currentValue}
                        onChange={handleRangeChange}
                        disabled={disabled}
                        className="custom-range-slider"
                    />
                </div>

                <div className="mt-4 flex w-full justify-between text-sm text-neutral-600">
                    <div className="flex flex-col items-start">
                        <span>{formatValue(min)}</span>
                        {minSuggested && minSuggested > min && (
                            <span className="mt-2 bg-primary-200 px-1 text-sm">
                                {t('general.minSuggested')}: {formatValue(minSuggested)}
                            </span>
                        )}
                    </div>
                    <div className="flex flex-col items-end">
                        <span>{formatValue(max)}</span>
                    </div>
                </div>
            </div>

            {footerContent && <div className="pt-6">{footerContent}</div>}
        </div>
    );
}
